import type {
  WorkerDocument,
  WorkerDocumentList,
  WorkerDocumentRetentionRule,
  WorkerDocumentType,
} from "@crewledger/shared/schemas";
import {
  apiFetch,
  downloadAuthenticatedFile,
  uploadAuthenticatedFormData,
  viewAuthenticatedFile,
} from "@/lib/api";

export async function fetchWorkerDocuments(workerId: string) {
  return apiFetch<{ data: WorkerDocumentList }>(`/api/workers/${workerId}/documents`);
}

export async function uploadWorkerDocument(
  workerId: string,
  input: {
    file: File;
    documentType: WorkerDocumentType;
    expiresAt?: string | null;
    retentionRule?: WorkerDocumentRetentionRule;
    retentionUntil?: string | null;
    notes?: string | null;
  },
) {
  const formData = new FormData();
  formData.set("file", input.file);
  formData.set("documentType", input.documentType);
  if (input.expiresAt) formData.set("expiresAt", input.expiresAt);
  if (input.retentionRule) formData.set("retentionRule", input.retentionRule);
  if (input.retentionUntil) formData.set("retentionUntil", input.retentionUntil);
  if (input.notes?.trim()) formData.set("notes", input.notes.trim());

  return uploadAuthenticatedFormData<{ data: WorkerDocument }>(
    `/api/workers/${workerId}/documents`,
    formData,
  );
}

export async function viewWorkerDocument(workerId: string, documentId: string) {
  await viewAuthenticatedFile(
    `/api/workers/${workerId}/documents/${documentId}/view`,
  );
}

export async function replaceWorkerDocument(
  workerId: string,
  documentId: string,
  file: File,
) {
  const formData = new FormData();
  formData.set("file", file);
  return uploadAuthenticatedFormData<{ data: WorkerDocument }>(
    `/api/workers/${workerId}/documents/${documentId}/replace`,
    formData,
  );
}

export async function deleteWorkerDocument(
  workerId: string,
  documentId: string,
  deletionReason: string,
) {
  return apiFetch<{ data: { deleted: true } }>(
    `/api/workers/${workerId}/documents/${documentId}`,
    {
      method: "DELETE",
      body: JSON.stringify({ deletionReason }),
    },
  );
}

export async function downloadWorkerDocument(
  workerId: string,
  documentId: string,
  fallbackFileName: string,
  reason?: string,
) {
  await downloadAuthenticatedFile(
    `/api/workers/${workerId}/documents/${documentId}/download`,
    fallbackFileName,
    reason?.trim() ? { "X-Document-Download-Reason": reason.trim() } : undefined,
  );
}
